import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ProductTypes } from '@app/core/_constant/product-type.constants';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { InventoryService } from '@app/core/inventory/services/inventory.service';
import { JobStatusService } from '@app/core/job-status/services/job-status.service';
import { KitService } from '@app/core/kits/services/kit.service';
import { LocationService } from '@app/core/locations/services/location.service';
import { WarehouseGroupService } from '@app/core/locations/services/warehouse-group.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { SubCategoryService } from '@app/core/products/services/subcategory.service';
import { RFQ, RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { ExportRfqService } from '@app/shared/services/export-rfq.service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { UIStateService } from '@app/shared/services/ui-state.service';
import { JobStatusDetailsPopupComponent } from '@app/shared/components/job-status/job-status-details-popup/job-status-details-popup.component';
import {
  DialogConfig,
  RfqInventoryOrdersTableComponent,
  UiConfirmationDialogComponent,
} from 'v2/modules';
import { IWarehouseGroup } from 'v2/modules/shared/interfaces/location-warehouse.interface';

@Component({
  selector: 'cnc-stocks',
  templateUrl: './stocks.component.html',
  styleUrls: ['./stocks.component.scss'],
})
export class StocksComponent implements OnInit, OnDestroy {
  initialize :boolean = false;
  loading :boolean = false;
  ftLotTracking :boolean = false;
  loadingWh :boolean = true;
  length = 0;
  pageSize = 20;
  pageSizeOptions :number[] = [5, 10, 25, 100];
  searchKey :string = '';
  pageTitle :string = 'Purchase Orders';

  HIDE_UOM_KEY :string = 'hide_uom';

  filter :any = {
    discontinued: 0, // show all active skus by default
  };

  params :any = {};

  recordSub :Subscription;
  records :any = { data: [] };

  totalRfqsCount :number = 0;

  AllWarehouses :any = [];
  warehouses :any = [];

  ftLocationBins :boolean = false;
  showLocations :boolean = false;
  hideUom :boolean = false;

  selectedWarehouseId :number[];

  private onDestroy$ = new Subject();
  warehouseGroups$ :Observable<IWarehouseGroup[]>;
  warehouseGroupLoading$ :Observable<boolean>;
  warehouseGroups :IWarehouseGroup[] = [];
  productTypes = ProductTypes;

  constructor(
    private invSvc :InventoryService,
    private cd :ChangeDetectorRef,
    private rfqSvc :RfqService,
    public exportRfq :ExportRfqService,
    public kitSvc :KitService,
    public categorySvc :CategoryService,
    public subcategorySvc :SubCategoryService,
    private locationSvc :LocationService,
    private translate :TranslateService,
    private toastr :ToastrService,
    public UserPermission :PermissionService,
    private settingsService :SettingsService,
    private uiStateSvc :UIStateService,
    private featureToggleService :FeatureToggleService,
    private warehouseGroupSvc :WarehouseGroupService,
    private dialog :MatLegacyDialog,
    private jobStatusService :JobStatusService,
  ) {
    this.settingsService
      .getKeyValue('track_inventory_by_location')
      .subscribe((settingEnabled :boolean) => {
        this.ftLocationBins = settingEnabled;
      });
  }

  ngOnInit() {
    this.getHideUomStatus();
    this.getDataFromService();
    this.setFiltersOnInit();
    this.getRecords();
    this.getWarehouses();
    this.getAllWarehouseGroup();
    this.getWarehouseGroup().subscribe();
    this.selectedWarehouseId = new Array<number>();
    if (this.isLotTrackingEnabled()) {
      this.ftLotTracking = true;
    }
  }

  ngOnDestroy() {
    this.recordSub.unsubscribe();
  }

  private getWarehouses() :void {
    this.locationSvc.getAll({ all: 1, parent_id: 0 }).subscribe((resp) => {
      this.AllWarehouses = resp.map((e) => {
        return {
          id: e.id,
          locationName: e.location_name,
        };
      });

      if (
        !this.filter.warehouseGroupIds ||
        this.filter.warehouseGroupIds.length === 0 ||
        this.filter.warehouseGroupIds === null
      ) {
        this.warehouses = this.AllWarehouses;
      }

      this.loadingWh = false;
      this.cd.detectChanges();
    });
  }

  getRecords(event ?:any, page ?:number, paramsOnly ?:boolean) {
    if (this.initialize) {
      return;
    }

    if (page) {
      this.params.page = page;
    }

    this.params = Object.assign(this.params, this.filter);

    this.params = Object.entries(this.params).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {},
    ); // remove falsy filter values

    if (event) {
      this.params.page = event.pageIndex + 1;
      this.params.per_page = event.pageSize;
    }

    if (paramsOnly) {
      return this.params;
    }

    this.uiStateSvc.setState('inventoryStocks', this.params);

    this.params = {
      ...this.params,
      ...(this.params.subcategoryIds && {
        subcategoryIds:
          this.params.subcategoryIds && this.params.subcategoryIds.length > 0
            ? this.params.subcategoryIds.join(', ')
            : '',
      }),
      ...(this.params.categoryId && {
        categoryId:
          this.params.categoryId && this.params.categoryId.length > 0
            ? this.params.categoryId.join(', ')
            : '',
      }),
      ...(this.params.warehouseIds && {
        warehouseIds:
          this.params.warehouseIds && this.params.warehouseIds.length > 0
            ? this.params.warehouseIds.join(', ')
            : '',
      }),
      ...(this.params.warehouseGroupIds && {
        warehouseGroupIds:
          this.params.warehouseGroupIds && this.params.warehouseGroupIds.length > 0
            ? this.params.warehouseGroupIds.join(', ')
            : '',
      }),
    };

    this.loading = true;
    this.recordSub = this.invSvc.get(this.params).subscribe((resp :any) => {
      if (resp.data) {
        this.totalRfqsCount = resp.total;

        this.records = resp;
        this.records.data.forEach((row) => {
          this.calculateStockQuantities(row);
        });
      }
      this.loading = false;
      this.cd.detectChanges();
    });

    this.categorySvc.getProductCategories();
    this.subcategorySvc.get();
    this.getWarehouses();
  }

  resetFilters() :void {
    this.filter = {
      discontinued: 0, // show all active skus by default
    };

    this.params = {
      per_page: 100,
      page: 1,
    };

    this.getRecords(false, 1);
  }

  exportRfqNumber() {
    Swal.fire('Exporting Inventory Stocks - RFQ, please wait...');
    Swal.showLoading();
    this.getSkusPagingated({
      page: 1,
      skus: [],
      type: 'rfq',
      showLocations: this.showLocations,
    });
  }

  exportKitNumber() {
    Swal.fire('Exporting Inventory Stocks - Kit, please wait...');
    Swal.showLoading();
    this.getSkusPagingated({
      page: 1,
      skus: [],
      type: 'kit',
      showLocations: false,
    });
  }

  private getHideUomStatus() :void {
    this.settingsService
      .getKeyValue(this.HIDE_UOM_KEY)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        if (resp) {
          this.hideUom = resp;
        }
        this.cd.detectChanges();
      });
  }

  private getSkusPagingated(data :{
    page :number;
    skus :any[];
    type :any;
    showLocations :boolean;
  }) :void {
    const { page, skus, type, showLocations } = data;
    const params = Object.assign({}, this.getRecords(false, 0, true));
    const lastPage = Math.ceil(this.totalRfqsCount / 1000);

    params.page = page;
    params.per_page = 1000;

    // Transform parameters
    this.transformParams(params);

    if (type == 'rfq') {
      this.invSvc.export(params).subscribe((data :any) => {
        if (data && data.data) {
          data.data.forEach((row) => {
            this.calculateStockQuantities(row);
          });

          const currentSkus = [...skus, ...data.data];
          if (page < lastPage && data.data.length > 0) {
            this.getSkusPagingated({
              page: page + 1,
              skus: currentSkus,
              type: 'rfq',
              showLocations: showLocations,
            });
          } else {
            this.exportRfq.exportInvStocksRfq(currentSkus, showLocations);
          }
        }
      });
    } else if (type == 'kit') {
      params.product_type = this.productTypes.KIT;
      this.rfqSvc
        .getV2Skus(params)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((data :any) => {
          if (data && data.data) {
            data.data.forEach((row) => {
              row.qty_incoming = Math.max(0, row.qty_incoming);
              row.qty_pending = Math.max(0, row.qty_pending);
              row.qty_onhand = Math.max(0, row.qty_onhand);
              row.qty_available = row.qty_onhand - row.qty_pending;
              row.unit_costs = this.kitSvc.getSkuUnitCosts(row);
            });

            const currentSkus = [...skus, ...data.data];
            if (page < lastPage && data.data.length > 0) {
              this.getSkusPagingated({
                page: page + 1,
                skus: currentSkus,
                type: 'kit',
                showLocations: false,
              });
            } else {
              this.exportRfq.exportInvStocksKit(currentSkus);
            }
          }
        });
    }
  }

  getSelectedWarehouseId(e :any, id :number) :void {
    if (e.target.checked) {
      this.selectedWarehouseId.push(id);
    } else {
      this.selectedWarehouseId = this.selectedWarehouseId.filter((m) => m != id);
    }
  }

  filterLocations(items) {
    return items.filter((stock) => stock.warehouse);
    // return items;
  }

  resetSelectedWarehouse() :void {
    if (this.selectedWarehouseId.length === 0) {
      return;
    }
    Swal.fire({
      title: 'Are You Sure?',
      text: 'Selecting this option will set the On Hand quantities for ALL Skus within the selected warehouse(s) to zero.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.value) {
        this.rfqSvc
          .resetBaselineToZero(this.selectedWarehouseId)
          .subscribe((resp :any) => {
            this.toastr.success(resp?.success, 'Success');
          });
      }
    });
  }

  recountPending() :void {
    Swal.fire({
      title: 'Are you sure you want to resync pending?',
      text: 'By clicking YES, Luminous will look at all currently open orders and will run a full pending resync across all SKUs. This action cannot be undone.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Exit',
    }).then((result) => {
      if (result.value) {
        Swal.fire({
          backdrop: true,
          allowOutsideClick: false,
          title: 'Processing request',
          text: 'This may take some time, please wait...',
        });
        Swal.showLoading();
        this.invSvc.recountPending().subscribe(
          (resp :any) => {
            Swal.fire(
              'Success',
              'Pending quantities are now updated.',
              'success',
            ).then(() => {
              this.getRecords();
            });
            Swal.hideLoading();
          },
          (error) => {
            Swal.fire(
              'Error',
              'Error processing request. Try again later.',
              'error',
            );
            Swal.hideLoading();
          },
        );
      }
    });
  }

  pullExternalWarehouseStocks() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Pull External Warehouse Stocks',
          message: 'Are you sure you want to pull external warehouse stocks?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.warehouseGroupSvc
            .pullExternalWarehouseStocks()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (resp) => {
                this.toastr.success(resp?.success, 'Success');
                
                // Open job status popup and start polling
                this.showJobStatusAndPoll();
              },
              error: (error) => {
                this.toastr.error(
                  'Error pulling external warehouse stocks',
                  'Error',
                );
              },
            });
        }
      });
  }

  private showJobStatusAndPoll() :void {
    // Open the job status popup
    const dialogRef = this.dialog.open(JobStatusDetailsPopupComponent, {
      width: '850px',
      disableClose: true,
    });

    // Start polling for jobs immediately
    this.jobStatusService.getJobs();
    
    // Force a refresh to pick up any new jobs
    setTimeout(() => {
      this.jobStatusService.getJobs();
    }, 1000);
  }

  setFiltersOnInit() :void {
    const stateView = this.uiStateSvc.getState('inventoryStocks');
    if (stateView) {
      this.filter = stateView;
      this.setFiltersOnInitWarehouseGroup();
    }
  }

  setFiltersOnInitWarehouseGroup() :void {
    const stateViewSelectedWarehouseGroups = this.uiStateSvc.getState(
      'selectedWarehouseGroups',
    );
    const tempWarehouses = [];

    if (
      this.filter.warehouseGroupIds &&
      stateViewSelectedWarehouseGroups &&
      stateViewSelectedWarehouseGroups[0]
    ) {
      stateViewSelectedWarehouseGroups.forEach(function (warehouseGroup) {
        if (warehouseGroup.warehouses && warehouseGroup.warehouses[0]) {
          warehouseGroup.warehouses.forEach(function (warehouse) {
            if (
              !tempWarehouses.find(
                (tempWarehouse) => tempWarehouse.id === warehouse.id,
              )
            ) {
              tempWarehouses.push(warehouse);
            }
          });
        }
      });
      this.warehouses = tempWarehouses;
    }
  }

  private isLotTrackingEnabled() :boolean {
    return this.featureToggleService.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  private getWarehouseGroup() :Observable<IWarehouseGroup[]> {
    return this.warehouseGroups$.pipe(
      tap((warehouseGroups) => {
        this.warehouseGroups = warehouseGroups;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  trackWarehouseGroupByFn(item :IWarehouseGroup) :number {
    return item.id;
  }

  private getAllWarehouseGroup() :void {
    this.warehouseGroupSvc.getAll({
      relations: 'warehouses',
      all: 'true',
    });
  }

  private getDataFromService() :void {
    this.warehouseGroups$ = this.warehouseGroupSvc.warehouseGroup$;
    this.warehouseGroupLoading$ = this.warehouseGroupSvc.loading$;
  }

  onSelectWarehouseGroup(warehouseGroups :IWarehouseGroup[]) :void {
    this.warehouses = [];
    this.filter.warehouseIds = [];
    const tempWarehouses = [];

    if (warehouseGroups && warehouseGroups[0]) {
      warehouseGroups.forEach(function (warehouseGroup) {
        if (warehouseGroup.warehouses && warehouseGroup.warehouses[0]) {
          warehouseGroup.warehouses.forEach(function (warehouse) {
            if (
              !tempWarehouses.find(
                (tempWarehouse) => tempWarehouse.id === warehouse.id,
              )
            ) {
              tempWarehouses.push(warehouse);
            }
          });
        }
      });
    }

    this.warehouses = tempWarehouses;

    this.uiStateSvc.setState('selectedWarehouseGroups', warehouseGroups);

    this.getRecords(false, 1);
  }

  onResetWarehouseGroupFilter() :void {
    this.warehouses = [];
    this.filter.warehouseIds = [];
    if (!this.filter.warehouseGroupIds || this.filter.warehouseGroupIds === null) {
      this.warehouses = this.AllWarehouses;
      this.uiStateSvc.setState('selectedWarehouseGroups', []);
    }

    this.getRecords(false, 1);
  }

  private calculateStockQuantities(row :any) :void {
    const filteredByWarehouseOrWarehouseGroupIds =
      this.params.warehouseIds || this.params.warehouseGroupIds;

    row.qty_incoming = Number(row.qty_incoming) > 0 ? Number(row.qty_incoming) : 0;
    row.qty_pending = Number(row.qty_pending) > 0 ? Number(row.qty_pending) : 0;
    row.qty_onhand = Number(row.qty_onhand) > 0 ? Number(row.qty_onhand) : 0;
    row.qty_available = Number(row.qty_onhand) - Number(row.qty_pending);

    if (filteredByWarehouseOrWarehouseGroupIds) {
      row.stock.qty_incoming = 0;
      row.stock.qty_pending = 0;
      row.stock.qty_onhand = 0;
      row.stock.qty_available = 0;
    }

    row.location_stocks.forEach((stock :any) => {
      if (filteredByWarehouseOrWarehouseGroupIds) {
        row.stock.qty_incoming += Number(stock.qty_incoming);
        row.stock.qty_pending += Number(stock.qty_pending);
        row.stock.qty_onhand += Number(stock.qty_onhand);
        row.stock.qty_available +=
          Number(stock.qty_onhand) - Number(stock.qty_pending);
      }

      stock.qty_incoming =
        Number(stock.qty_incoming) > 0 ? Number(stock.qty_incoming) : 0;
      stock.qty_pending =
        Number(stock.qty_pending) > 0 ? Number(stock.qty_pending) : 0;
      stock.qty_onhand =
        Number(stock.qty_onhand) > 0 ? Number(stock.qty_onhand) : 0;
      stock.qty_available = Number(stock.qty_onhand) - Number(stock.qty_pending);
    });

    if (filteredByWarehouseOrWarehouseGroupIds) {
      row.qty_incoming =
        Number(row.stock.qty_incoming) > 0 ? Number(row.stock.qty_incoming) : 0;
      row.qty_pending =
        Number(row.stock.qty_pending) > 0 ? Number(row.stock.qty_pending) : 0;
      row.qty_onhand =
        Number(row.stock.qty_onhand) > 0 ? Number(row.stock.qty_onhand) : 0;
      row.qty_available = Number(row.stock.qty_available);
    }

    row.unit_costs = this.rfqSvc.getSkuUnitCosts(row);
  }

  private transformParams(params :any) :void {
    if (params.categoryId && params.categoryId.length > 0) {
      params.categoryId = params.categoryId.join(', ');
    }
    if (params.subcategoryIds && params.subcategoryIds.length > 0) {
      params.subcategoryIds = params.subcategoryIds.join(', ');
    }
    if (params.warehouseIds && params.warehouseIds.length > 0) {
      params.warehouseIds = params.warehouseIds.join(', ');
    }
    if (params.warehouseGroupIds && params.warehouseGroupIds.length > 0) {
      params.warehouseGroupIds = params.warehouseGroupIds.join(', ');
    }
  }

  onViewIncomingRfqStockDetails(warehouseId :number, rfq :RFQ) :void {
    this.dialog.open(RfqInventoryOrdersTableComponent, {
      data: {
        rfq,
        warehouseId,
      },
      width: DialogConfig.Size.LARGE,
      disableClose: true,
    });
  }
}
